// SiteFooter — corporate-phat site footer.
//
// Three bands stacked vertically:
//   1. Top band — dark sage background. Brand block on the left
//      (logo + tagline + a one-line lede); four-column site map on
//      the right (Learn / Specs / Research / Community).
//   2. Middle band — slightly lighter sage. "Stay current" eyebrow
//      and the six EngagementPillRow brand pills (LinkedIn / Substack /
//      Reddit / Medium / TamedAutonomy / Publish-here disabled).
//   3. Bottom band — darkest sage. Copyright + published-by + github +
//      the speakeasy colophon link.
//
// Speakeasy delight 05 — "the door remembers." The colophon link
// continues to honor the localStorage flag (ktp.speakeasy.entered.v1)
// and the "every room read" recognition. Two-tier opacity / accent-2
// underline behaviour preserved exactly.
//
// Mobile: the four-column site map collapses to two columns at
// ≤720px and to one at ≤480px via the constitution-strip pattern
// (see tokens.css for the responsive rules).

import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useJourney } from "../hooks/useJourney";
import { ENGAGEMENT_PILLS, EngagementPillRow } from "./cta";
import { openSearch } from "./SearchButton";
import { prefetchSearchIndex } from "../lib/search-loader";
import { LinkMark } from "./icons";
import FooterSiteMap from "./FooterSiteMap";
import { HORIZON, SITE_LAST_LINE } from "../lib/theme";

const ENTERED_KEY = "ktp.speakeasy.entered.v1";

const ROOM_SLUGS = [
  "sentience",
  "feeling",
  "thoughts",
  "self",
  "axioms",
  "tensions",
  "motifs",
  "acequia",
  "what-next",
  "the-program",
];

/* ── Footer color tokens (inline so the tokens.css stays clean) ─ */

const SAGE_DEEP = "#1f2924"; // top band (brand + site map + stay current)
const SAGE_DARK = "#161e19"; // copyright strip
const SAGE_TEXT = "#cfd4cb"; // primary cream-on-sage
// Muted on sage. #909c94 clears WCAG 4.5:1 on both bands (5.3:1 on
// SAGE_DEEP, 6.0:1 on SAGE_DARK); the previous #7e8a82 sat at 4.17:1.
const SAGE_TEXT_DIM = "#909c94";
const SAGE_HEAD = "#fafaf7"; // headings cream
const SAGE_LINE = "#2e3a32"; // hairline divider
const SAGE_ACCENT = "#b88746"; // existing gold accent

export default function SiteFooter() {
  const [entered, setEntered] = useState(false);

  const journey = useJourney();
  const completed = ROOM_SLUGS.every((slug) =>
    journey.isRead(`room:${slug}`)
  );

  useEffect(() => {
    try {
      if (localStorage.getItem(ENTERED_KEY) === "1") setEntered(true);
    } catch {
      /* ignore — private mode or quota */
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key === ENTERED_KEY && e.newValue === "1") setEntered(true);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <footer
      style={{
        marginTop: "auto",
        background: SAGE_DEEP,
        color: SAGE_TEXT,
        fontFamily: "var(--sans)",
      }}
    >
      <div aria-hidden style={{ height: 2, background: HORIZON }} />
      {/* ── 1. Top band — brand + site map + stay current ─────
           Single dark-sage band. Brand block on the left, site map on
           the right, then stay-current strip beneath the site map (
           spanning full width under both columns). */}
      <div
        style={{
          padding: "56px var(--pad) 48px",
        }}
      >
        <div
          style={{
            maxWidth: "var(--frame-w)",
            margin: "0 auto",
          }}
        >
          <div
            className="site-footer-top"
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(260px, 1fr) minmax(0, 2fr)",
              gap: 56,
              marginBottom: 44,
            }}
          >
            <BrandBlock />
            <FooterSiteMap palette={{ head: SAGE_HEAD, text: SAGE_TEXT, dim: SAGE_TEXT_DIM, line: SAGE_LINE }} />
          </div>

          {/* Search — full-width affordance above the stay-current strip.
              A real button styled as a field; opens the one palette. */}
          <div className="site-footer-search" style={{ borderTop: `1px solid ${SAGE_LINE}`, paddingTop: 28, marginBottom: 28 }}>
            <div
              style={{
                fontFamily: "var(--mono)",
                fontSize: 10,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: SAGE_TEXT_DIM,
                marginBottom: 10,
              }}
            >
              search
            </div>
            <button
              type="button"
              onClick={openSearch}
              onMouseEnter={() => { void prefetchSearchIndex().catch(() => undefined); }}
              onFocus={() => { void prefetchSearchIndex().catch(() => undefined); }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "min(420px, 100%)",
                padding: "10px 14px",
                minHeight: 44,
                background: "transparent",
                border: `1px solid ${SAGE_LINE}`,
                color: SAGE_TEXT_DIM,
                fontFamily: "var(--mono)",
                fontSize: 11,
                letterSpacing: 0.5,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <span aria-hidden style={{ fontSize: 13 }}>⌕</span>
              search pages, terms, essays, the record…
            </button>
          </div>

          {/* Stay current — full-width strip beneath the grid. Same
              left edge as the brand block above. */}
          <div
            style={{
              borderTop: `1px solid ${SAGE_LINE}`,
              paddingTop: 28,
            }}
          >
            <div
              style={{
                fontFamily: "var(--mono)",
                fontSize: 10,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: SAGE_TEXT_DIM,
                marginBottom: 12,
              }}
            >
              stay current
            </div>
            <p
              className="site-footer-stay-lede"
              style={{
                fontFamily: "var(--sans)",
                fontSize: 14,
                color: SAGE_TEXT,
                lineHeight: 1.65,
                maxWidth: 720,
                margin: "0 0 18px 0",
              }}
            >
              Short-form Digital Gravity for Enterprise on LinkedIn.
              Lyrical essays on Medium. Kinetic Trust Protocol details on
              Substack. All of it open on Reddit. Pieces end up where they
              end up — pick the rhythm that fits.
            </p>
            <div className="site-footer-pills"><EngagementPillRow /></div>
            {/* Phones: the same live links as quiet text, as the desert footer does. */}
            <div className="site-footer-stay-links" style={{ flexWrap: "wrap", gap: "10px 22px", fontSize: 14 }}>
              {ENGAGEMENT_PILLS.filter((p) => p.kind === "live").map((p) => (
                <a key={p.label} href={p.href} target="_blank" rel="noopener noreferrer" style={{ color: SAGE_TEXT, textDecoration: "underline", textDecorationColor: "rgba(207, 212, 203, 0.35)", textUnderlineOffset: 3 }}>
                  {p.label}<LinkMark />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── 2. Bottom band — copyright strip ────────── */}
      <div
        className="site-footer-bot"
        style={{
          background: SAGE_DARK,
          padding: "16px var(--pad)",
          fontFamily: "var(--mono)",
          fontSize: 10,
          letterSpacing: 0.4,
          color: SAGE_TEXT_DIM,
        }}
      >
        <div
          style={{
            maxWidth: "var(--frame-w)",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            gap: 22,
            flexWrap: "wrap",
          }}
        >
          <span>the study of resilient systems</span>
          <span aria-hidden style={{ color: SAGE_LINE }}>
            ·
          </span>
          <span>© 2026</span>
          <span style={{ flex: 1 }} />
          <a
            href="/feed.xml"
            title="RSS feed — recent essays"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "2px 8px",
              border: `1px solid ${SAGE_LINE}`,
              borderRadius: 3,
              color: SAGE_ACCENT,
              fontFamily: "var(--mono)",
              fontSize: 9,
              letterSpacing: 1,
              textTransform: "uppercase",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            <span style={{ fontSize: 10 }}>◉</span> RSS
          </a>
          <a
            href="https://github.com/nmcitra"
            target="_blank"
            rel="noreferrer"
            style={{ color: SAGE_TEXT, textDecoration: "none" }}
          >
            github<LinkMark />
          </a>
          <Link
            href="/colophon"
            aria-label={
              completed
                ? "colophon (every room)"
                : entered
                ? "colophon (you've been here)"
                : "colophon"
            }
            style={{
              color: SAGE_TEXT_DIM,
              // WCAG floor for the two-tier "door remembers" dimming:
              // 0.55 blended the 10px label to 2.33:1. At 0.9 over
              // SAGE_DARK the label holds ~5.1:1; the tier signal now
              // lives mostly in the π glyph color below.
              opacity: entered ? 0.95 : 0.9,
              textDecoration: "none",
              borderBottom: completed
                ? `1px solid ${SAGE_ACCENT}`
                : "1px solid transparent",
              paddingBottom: 1,
              display: "inline-flex",
              alignItems: "baseline",
              gap: 6,
              transition:
                "opacity 400ms var(--ease-out-soft), border-color 600ms var(--ease-out-soft)",
            }}
          >
            {/* The Net (1995) — the π glyph as the unmarked door. The
                pairing keeps the link readable while planting the
                reference for those who recognize it. Three tiers now
                read in the glyph: dim (never entered) → cream (been
                here) → gold (every room). All three clear 4.5:1. */}
            <span
              aria-hidden
              style={{
                fontFamily: "var(--sans)",
                fontSize: 13,
                lineHeight: 1,
                color: completed
                  ? SAGE_ACCENT
                  : entered
                  ? SAGE_TEXT
                  : SAGE_TEXT_DIM,
                transition: "color 600ms var(--ease-out-soft)",
              }}
            >
              π
            </span>
            colophon
          </Link>
        </div>
        {/* The site's last line, as the Desert Book pages end (Chris, 2026-10-27). */}
        <p style={{ maxWidth: "var(--frame-w)", margin: "12px auto 0", fontFamily: "var(--sans)", fontSize: 14, letterSpacing: 0, lineHeight: 1.5, color: SAGE_TEXT, fontStyle: "italic" }}>
          {SITE_LAST_LINE}
        </p>
      </div>
    </footer>
  );
}

/* ── Brand block (left of top band) ───────────────────── */

function BrandBlock() {
  return (
    <div>
      <Link
        href="/"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          color: SAGE_HEAD,
          textDecoration: "none",
          fontFamily: "var(--mono)",
          fontWeight: 600,
          fontSize: 14,
          letterSpacing: 1.2,
          marginBottom: 18,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 22,
            height: 22,
            border: `1.5px solid ${SAGE_HEAD}`,
            borderRadius: "50%",
            position: "relative",
            display: "inline-block",
          }}
        >
          <span
            style={{
              position: "absolute",
              inset: 5,
              border: `1px solid ${SAGE_HEAD}`,
              borderRadius: "50%",
            }}
          />
        </span>
        KTP/
      </Link>
      <p
        style={{
          fontFamily: "var(--sans)",
          fontSize: 14,
          color: SAGE_TEXT,
          lineHeight: 1.65,
          maxWidth: 360,
          margin: "0 0 18px 0",
        }}
      >
        Trust in motion — across code, classrooms, institutions, and
        minds. KTP is what governance looks like when machines do the
        acting.
      </p>
      <p
        style={{
          fontFamily: "var(--mono)",
          fontSize: 10,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: SAGE_TEXT_DIM,
          margin: "0 0 6px 0",
        }}
      >
        version
      </p>
      <p
        style={{
          fontFamily: "var(--mono)",
          fontSize: 12,
          color: SAGE_TEXT,
          margin: "0 0 28px 0",
        }}
      >
        rfc v2.1.0 · gödel ·{" "}
        <a
          href="https://github.com/nmcitra/ktp-rfc/releases/tag/v2.1.0"
          target="_blank"
          rel="noreferrer"
          style={{
            color: SAGE_ACCENT,
            textDecoration: "none",
          }}
        >
          release notes<LinkMark />
        </a>
      </p>
      <p
        style={{
          fontFamily: "var(--sans)",
          fontSize: 13,
          color: SAGE_TEXT_DIM,
          lineHeight: 1.6,
          margin: 0,
        }}
      >
        <a
          href="https://tamedautonomy.ai/"
          target="_blank"
          rel="noreferrer"
          style={{
            color: SAGE_TEXT,
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Tamed Autonomy
        </a>
        {" "}— the KTP implementation project.{" "}
        <a
          href="https://tamedautonomy.ai/"
          target="_blank"
          rel="noreferrer"
          style={{
            color: SAGE_TEXT_DIM,
            textDecoration: "none",
          }}
        >
          tamedautonomy.ai<LinkMark />
        </a>
      </p>
    </div>
  );
}



